import { Node, Edge } from "reactflow";

export interface NodeOutput {
  configData?: any;
  response?: any;
  outputData?: any;
  label?: string;
  header?: string;
  nodeType?: string;
}

export const getConnectedInputs = (
  nodeId: string,
  edges: Edge[],
  nodes: Node[],
): NodeOutput[] => {
  // Find all edges connected to this node's input
  const incomingEdges = edges.filter((edge) => edge.target === nodeId);

  if (incomingEdges.length === 0) {
    return [];
  }

  // Get data from all source nodes
  const inputs = incomingEdges
    .map((edge) => {
      const sourceNode = nodes.find((n) => n.id === edge.source);
      if (!sourceNode) return null;

      // Check if source is an if_else node
      const isIfElseNode =
        sourceNode.type === "ifElseNode" ||
        sourceNode.data?.sub_type === "if_else";

      // Check if source is a loop node
      const isLoopNode =
        sourceNode.type === "loopOverItemsNode" ||
        sourceNode.data?.sub_type === "loop_over_items";

      // Check if source is a switch node
      const isSwitchNode =
        sourceNode.type === "switchNode" ||
        sourceNode.data?.sub_type === "switch_case";

      let responseData = sourceNode.data?.response;
      let outputData = sourceNode.data?.outputData;

      // Helper function to extract json_data from items
      const extractJsonData = (data: any): any => {
        if (!data) return data;

        if (Array.isArray(data)) {
          return data.map((item: any) => {
            if (item && typeof item === "object" && "json_data" in item) {
              return item.json_data;
            }
            return item;
          });
        }

        if (data && typeof data === "object" && "json_data" in data) {
          return data.json_data;
        }

        return data;
      };

      // SWITCH NODE DATA FILTERING
      // For switch nodes, filter data based on which handle is connected
      // Switch format: [{rule, ruleIndex, items, itemCount}, ...]
      // sourceHandle format: "output_0", "output_1", "output_2", etc.

      const isSwitchFormat =
        isSwitchNode &&
        Array.isArray(responseData) &&
        responseData.length > 0 &&
        responseData[0] !== null &&
        typeof responseData[0] === "object" &&
        "rule" in responseData[0] &&
        "items" in responseData[0];

      if (isSwitchFormat) {
        // Extract index from sourceHandle (e.g., "output_2" -> 2)
        let handleIndex = 0; // Default to first rule if no handle specified

        if (edge.sourceHandle) {
          const handleMatch = edge.sourceHandle.match(/output_(\d+)/);
          if (handleMatch) {
            handleIndex = parseInt(handleMatch[1], 10);
          }
        }

        const ruleData = responseData[handleIndex];

        if (ruleData && Array.isArray(ruleData.items)) {
          // Pass only this rule's items (already unwrapped in switch format)
          responseData = ruleData.items;
        } else {
          // Handle doesn't have data - pass empty array
          responseData = [];
        }
      }

      // Same filtering for outputData
      const isSwitchOutputFormat =
        isSwitchNode &&
        Array.isArray(outputData) &&
        outputData.length > 0 &&
        outputData[0] !== null &&
        typeof outputData[0] === "object" &&
        "rule" in outputData[0] &&
        "items" in outputData[0];

      if (isSwitchOutputFormat) {
        // Extract index from sourceHandle (e.g., "output_2" -> 2)
        let handleIndex = 0; // Default to first rule if no handle specified

        if (edge.sourceHandle) {
          const handleMatch = edge.sourceHandle.match(/output_(\d+)/);
          if (handleMatch) {
            handleIndex = parseInt(handleMatch[1], 10);
          }
        }

        const ruleData = outputData[handleIndex];

        if (ruleData && Array.isArray(ruleData.items)) {
          // Pass only this rule's items (already unwrapped in switch format)
          outputData = ruleData.items;
        } else {
          // Handle doesn't have data - pass empty array
          outputData = [];
        }
      }

      // IF-ELSE NODE DATA FILTERING
      const isIfElseFormat =
        isIfElseNode &&
        Array.isArray(responseData) &&
        responseData.length === 2 &&
        Array.isArray(responseData[0]) &&
        Array.isArray(responseData[1]);

      if (isIfElseFormat) {
        // Extract json_data from both arrays first
        const trueData = extractJsonData(responseData[0]);
        const falseData = extractJsonData(responseData[1]);

        // Filter based on sourceHandle
        if (edge.sourceHandle === "false") {
          // Only pass false data
          responseData = falseData;
        } else {
          // Default to true data (sourceHandle === "true" or undefined)
          responseData = trueData;
        }
      } else if (isLoopNode) {
        // For loop nodes, check if response has loop format: [doneArray, loopArray]
        const isLoopFormat =
          Array.isArray(responseData) &&
          responseData.length === 2 &&
          Array.isArray(responseData[0]) &&
          Array.isArray(responseData[1]);

        if (isLoopFormat) {
          // Extract json_data from both arrays first
          const doneData = extractJsonData(responseData[0]);
          const loopData = extractJsonData(responseData[1]);

          // Filter based on sourceHandle
          if (edge.sourceHandle === "loop") {
            // Only pass loop data
            responseData = loopData;
          } else {
            // Default to done data (sourceHandle === "done" or undefined)
            responseData = doneData;
          }
        } else {
          // For regular format, extract json_data
          responseData = extractJsonData(responseData);
        }
      } else {
        // For regular nodes, extract json_data
        responseData = extractJsonData(responseData);
      }

      // Same for outputData
      const isIfElseOutputFormat =
        isIfElseNode &&
        Array.isArray(outputData) &&
        outputData.length === 2 &&
        Array.isArray(outputData[0]) &&
        Array.isArray(outputData[1]);

      if (isIfElseOutputFormat) {
        // Extract json_data from both arrays first
        const trueData = extractJsonData(outputData[0]);
        const falseData = extractJsonData(outputData[1]);

        // Filter based on sourceHandle
        if (edge.sourceHandle === "false") {
          // Only pass false data
          outputData = falseData;
        } else {
          // Default to true data (sourceHandle === "true" or undefined)
          outputData = trueData;
        }
      } else if (isLoopNode) {
        // For loop nodes, check if output has loop format: [doneArray, loopArray]
        const isLoopOutputFormat =
          Array.isArray(outputData) &&
          outputData.length === 2 &&
          Array.isArray(outputData[0]) &&
          Array.isArray(outputData[1]);

        if (isLoopOutputFormat) {
          // Extract json_data from both arrays first
          const doneData = extractJsonData(outputData[0]);
          const loopData = extractJsonData(outputData[1]);

          // Filter based on sourceHandle
          if (edge.sourceHandle === "loop") {
            // Only pass loop data
            outputData = loopData;
          } else {
            // Default to done data (sourceHandle === "done" or undefined)
            outputData = doneData;
          }
        } else {
          // For regular format, extract json_data
          outputData = extractJsonData(outputData);
        }
      } else {
        // For regular nodes, extract json_data
        outputData = extractJsonData(outputData);
      }
      // Keep connected upstream nodes even when output is not available yet.
      // This prevents false "No Input Connected" states before execution.
      return {
        configData: sourceNode.data?.configData,
        response: responseData,
        outputData: outputData,
        label: sourceNode.data?.label || sourceNode.data?.header,
        header: sourceNode.data?.header,
        nodeType: sourceNode.type,
      } as NodeOutput;
    })
    .filter((input) => input !== null) as NodeOutput[];

  return inputs;
};

export const updateNodeOutput = (
  nodeId: string,
  response: any,
  onDataChange?: (id: string, updates: any) => void,
) => {
  if (onDataChange) {
    onDataChange(nodeId, { response });
  }
};

// function for getting execution path of nodes, including configuration nodes, based on edges and nodes in the workflow. This will be used to determine which nodes to execute when a particular node is triggered, ensuring that all necessary configuration nodes are included in the execution path.
export const getExecutionPath = (
  targetNodeId: string,
  nodes: Node[],
  edges: Edge[],
): Node[] => {
  // Filter out UI-only helper nodes (addButtonNode creates cycles and shouldn't be executed)
  // Include all other nodes (including configuration nodes) in the path
  const executableNodes = nodes.filter((node) => node.type !== "addButtonNode");

  // Build adjacency list and in-degree map
  const adjacencyList = new Map<string, string[]>();
  const inDegree = new Map<string, number>();

  // Initialize
  executableNodes.forEach((node) => {
    adjacencyList.set(node.id, []);
    inDegree.set(node.id, 0);
  });

  // Build graph (include all nodes)
  edges.forEach((edge) => {
    const sourceIsExecutable = executableNodes.some(
      (n) => n.id === edge.source,
    );
    const targetIsExecutable = executableNodes.some(
      (n) => n.id === edge.target,
    );

    if (sourceIsExecutable && targetIsExecutable) {
      adjacencyList.get(edge.source)?.push(edge.target);
      inDegree.set(edge.target, (inDegree.get(edge.target) || 0) + 1);
    }
  });

  // Find all nodes in the path to target using DFS backwards
  const nodesInPath = new Set<string>();
  const visited = new Set<string>();

  const dfsBackward = (nodeId: string) => {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);
    nodesInPath.add(nodeId);

    // Find all predecessors
    edges.forEach((edge) => {
      if (edge.target === nodeId) {
        const sourceIsExecutable = executableNodes.some(
          (n) => n.id === edge.source,
        );
        if (sourceIsExecutable) {
          dfsBackward(edge.source);
        }
      }
    });
  };

  // Start DFS from target node
  dfsBackward(targetNodeId);

  // Filter to only nodes in path
  const pathNodes = executableNodes.filter((node) => nodesInPath.has(node.id));

  // Topological sort using Kahn's algorithm on path nodes
  const queue: string[] = [];
  const pathInDegree = new Map<string, number>();

  // Initialize in-degree for path nodes
  pathNodes.forEach((node) => {
    pathInDegree.set(node.id, 0);
  });

  // Calculate in-degree for path nodes only
  edges.forEach((edge) => {
    if (nodesInPath.has(edge.source) && nodesInPath.has(edge.target)) {
      pathInDegree.set(edge.target, (pathInDegree.get(edge.target) || 0) + 1);
    }
  });

  // Find start nodes (in-degree 0) in path
  pathNodes.forEach((node) => {
    if (pathInDegree.get(node.id) === 0) {
      queue.push(node.id);
    }
  });

  const sortedNodeIds: string[] = [];

  while (queue.length > 0) {
    const nodeId = queue.shift()!;
    sortedNodeIds.push(nodeId);

    // Get neighbors in path
    adjacencyList.get(nodeId)?.forEach((neighborId) => {
      if (nodesInPath.has(neighborId)) {
        const newInDegree = (pathInDegree.get(neighborId) || 0) - 1;
        pathInDegree.set(neighborId, newInDegree);

        if (newInDegree === 0) {
          queue.push(neighborId);
        }
      }
    });
  }

  // Convert sorted IDs back to nodes
  const result = sortedNodeIds
    .map((id) => pathNodes.find((n) => n.id === id))
    .filter((node): node is Node => node !== undefined);

  return result;
};

/**
 * Filter out config nodes from execution and create bypass edges
 * Config nodes are included in saves/loads but excluded from execution
 * Returns: { executableNodes, bypassEdges, configNodeIds }
 */
export const filterConfigNodesForExecution = (
  nodes: Node[],
  edges: Edge[],
): {
  executableNodes: Node[];
  bypassEdges: Edge[];
  configNodeIds: Set<string>;
} => {
  // Identify config/authentication nodes
  const configNodeIds = new Set(
    nodes.filter((node) => node.type === "authentication").map((n) => n.id),
  );

  // Filter out config nodes
  const executableNodes = nodes.filter(
    (node) => node.type !== "authentication",
  );

  // Create bypass edges: if A -> Config -> B, create A -> B
  const bypassEdges: Edge[] = [];
  const executableNodeIds = new Set(executableNodes.map((n) => n.id));

  edges.forEach((edge) => {
    if (configNodeIds.has(edge.target)) {
      // This edge goes to a config node, find what comes after
      const configOutEdges = edges.filter((e) => e.source === edge.target);
      configOutEdges.forEach((outEdge) => {
        if (
          executableNodeIds.has(edge.source) &&
          executableNodeIds.has(outEdge.target)
        ) {
          // Create bypass edge
          bypassEdges.push({
            ...edge,
            id: `${edge.source}-bypass-${outEdge.target}`,
            target: outEdge.target,
            targetHandle: outEdge.targetHandle,
          } as Edge);
        }
      });
    }
  });

  return { executableNodes, bypassEdges, configNodeIds };
};
